import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DocumentModel } from '../document.model';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiCommentSettingType } from '@fi-shared/types';
import { TemplateLayoutLanguageService } from '@fi-app/template/template-layout-language.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { AppTextService } from '@fi-shared/app-text';
import { FiPrintDownloadComponent } from '@fi-shared/fi-print-download/fi-print-download.component';
import { MobileTabsModel } from '@fi-app/utilities/mobile-tabs/mobile-tabs.model';
import { MobileTabModel } from '@fi-app/utilities/mobile-tabs/mobile-tab.model';
import { Subject, timer } from 'rxjs';
import { DocumentService } from '../document.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { takeUntil } from 'rxjs/operators';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { ICommentModel } from '@fi-shared/models';
import { isIOS } from '@fi-shared/utils/isIOS';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { FiItemsListHelperService } from '@fi-shared/fi-items-list/fi-items-list-helper.service';
import { DocumentListDataService } from '@fi-app/document/document-list-data-service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { FiItemsEditHelperService } from '@fi-shared/fi-shared-services/fi-items-edit-helper.service';
import { isDefined } from '@fi-shared/utils';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { TemplateModel } from '@fi-app/template/template.model';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';

export interface IDocumentViewData {
    templateLayout: TemplateLayoutModel;
    languageId: number;
    documentTemplateLayout: TemplateLayoutModel;
    hasEditAccess: boolean;
    defaultLanguageAbsent: boolean;
}

@Component({
    selector: 'fi-document-view',
    templateUrl: './document-view.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./document-view.component.scss'],
    standalone: false,
})
export class FiDocumentViewComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input('data') documentViewData: IDocumentViewData;
    @Input('document') Document: DocumentModel;
    @Input() focusNewComment: boolean = false;
    @Input() commentId: number;
    @Output() langChange = new EventEmitter();
    @ViewChild(FiPrintDownloadComponent, { static: false }) printDownloadComponent: FiPrintDownloadComponent;

    EditTabs: EditTabsModel;

    templateLayout: TemplateLayoutModel;
    documentTemplateLayout: TemplateLayoutModel;
    selectedLanguageId: number;
    defaultLanguageAbsent: boolean;
    IsCopyEnabled: boolean;
    comments: ICommentModel[];
    DeviceSwitch = {
        selected: null,
    };
    Template: TemplateModel;
    ShowComments: boolean;
    WriteAccessRight: boolean;
    ModuleType;
    ObjectTypes;
    MoreMenuItems: MoreMenuMobileSettingsModel;
    MobileTabs = new MobileTabsModel();
    FiStateNames = FiStateNames;
    documentName: string;

    constructor(
        private stateAccessCheckService: StateAccessCheckService,
        public TemplateLayoutLanguageService: TemplateLayoutLanguageService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private documentService: DocumentService,
        private stateService: FiStateService,
        private contentLanguageService: ContentLanguagesService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private commentsService: FiCommentsService,
        private itemsListHelperService: FiItemsListHelperService,
        private documentListDataService: DocumentListDataService,
        private itemsDeleteHelperService: FiItemsDeleteHelperService,
        private itemsEditHelperService: FiItemsEditHelperService,
        private detectDevicesService: DetectDevicesService,
    ) {}

    ngOnInit() {
        this.updateDocumentName();
        this.EditTabs = new EditTabsModel(this.documentName, this.appTextService.get('back'));
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    initComments() {
        this.commentsService.getComments(this.Document.Id, ObjectTypes.Document).then((comments) => {
            this.comments = comments;
        });
    }

    deleteDocument() {
        this.itemsDeleteHelperService
            .documentDeleteConfirm([this.Document], this.appTextService.get('document.delete_permanently_confirm'))
            .then(() => {
                this.documentService.deleteDocuments([this.Document.Id]).then(() => {
                    this.showSnackbar();
                    window.history.back();
                });
            });
    }

    private showSnackbar() {
        const restoreDocumentFn = (restoreIds: number[]) => this.documentService.RestoreDocuments(restoreIds);
        const reloadListFn = () => this.documentService.doReloadDocumentList$();

        this.itemsDeleteHelperService.showDeletedObjectSnackBar(
            ObjectTypes.Document,
            [this.Document],
            this.Document.IsDeleted,
            restoreDocumentFn,
            reloadListFn,
        );
    }

    editDocument() {
        this.itemsEditHelperService.showEditRestoreConfirm(
            this.documentName,
            this.Document.IsDeleted,
            () =>
                this.stateService.go(FiStateNames.Document.Edit, {
                    Id: this.Document.Id,
                    languageId: this.selectedLanguageId,
                }),
            () => this.documentService.RestoreDocuments([this.Document.Id]),
        );
    }

    initMoreMenuMobile() {
        this.MoreMenuItems = new MoreMenuMobileSettingsModel();
        this.MoreMenuItems.AddListItem(this.appTextService.get('common.select_lang'), () => {
            const documentLayoutLangIds: number[] = this.TemplateLayoutLanguageService.GetLayoutLanguages(
                this.documentTemplateLayout,
            );
            this.contentLanguageService
                .openSelectLanguageDialog(this.selectedLanguageId, !this.defaultLanguageAbsent, documentLayoutLangIds)
                .onYesButtonClick((result) => {
                    this.mobileLanguageChanged(result);
                });
        });

        if (!isIOS())
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.print'), () =>
                this.printDownloadComponent.Print(),
            );

        this.MoreMenuItems.AddListItem(this.appTextService.get('common.download'), () =>
            this.printDownloadComponent.Download(),
        );

        if (this.IsCopyEnabled)
            this.MoreMenuItems.AddListItem(this.appTextService.get('copy'), () =>
                this.stateService.go(FiStateNames.Document.Copy, {
                    Id: this.Document.Id,
                    languageId: this.selectedLanguageId,
                }),
            );

        if (this.WriteAccessRight) {
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.edit'), () => this.editDocument());
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.delete'), () => this.deleteDocument());
        }
    }

    updateDocumentName() {
        const value = this.fiContentLanguageSharedService.getContentLanguageValueForDisplayContent(
            this.Document.Names,
            this.selectedLanguageId,
        );
        this.documentName = value;
        if (isDefined(this.EditTabs)) this.EditTabs.PageTitle = this.documentName;
    }

    mobileLanguageChanged(newLanuageId: number) {
        this.onLangChange(newLanuageId);
    }

    onLangChange($event) {
        this.selectedLanguageId = $event;
        if (!this.defaultLanguageAbsent) this.TemplateLayoutLanguageService.selectLanguage($event);

        this.langChange.emit($event);
        this.updateDocumentName();
    }

    // note: not used ?
    // documentRendered() {
    //     if (this.stateService.params.commentId) {
    //         const commentElement = $('#comment-' + this.stateService.params.commentId),
    //             commentsListElement = commentElement.closest('ccm-comments-list'),
    //             scrollElement = commentsListElement.closest('[fi-scroll-root]'),
    //             documentElement = scrollElement.find('.Ccm-Page-Document');
    //
    //         if (commentElement.length > 0
    //             && commentsListElement.height() + documentElement.height() > scrollElement.height())
    //                 commentElement.get(0).scrollIntoView();
    //     }
    // };

    onDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    ngAfterViewInit() {
        const _timer = timer(4000);
        _timer.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.documentService.MarkDocumentAsVisited(this.Document.Id);
        });
    }

    ngOnChanges() {
        if (this.documentViewData && this.Document) this._init();
    }

    _init() {
        this.templateLayout = this.documentViewData.templateLayout;
        this.documentTemplateLayout = this.documentViewData.documentTemplateLayout;
        this.selectedLanguageId = this.documentViewData.languageId;
        this.defaultLanguageAbsent = this.documentViewData.defaultLanguageAbsent;
        this.IsCopyEnabled = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Document.Add);
        this.initComments();
        this.Template = this.Document.Template;
        this.ShowComments = this.Document.DocumentSetting.CommentSettingType == FiCommentSettingType.AllowAllComments;
        this.WriteAccessRight = this.documentViewData.hasEditAccess;
        this.ModuleType = ModuleType;
        this.ObjectTypes = ObjectTypes;
        this.initMoreMenuMobile();
        this.MobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.pages'), 'DocumentTab'));
        this.MobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.more'), 'MoreTab'));
    }
}
